/* eslint-disable @typescript-eslint/no-explicit-any */
import isEmpty from 'lodash/isEmpty';
import { IConsumerDataResponse } from '../../consumerUxlApis/types/consumerUXlResponse.types';
import { IPromotionEdgeNode } from '../../consumerUxlApis/types/promotions.types';
import {
  AUTH_STATE,
  CONSUMER_PROMOTION_CONFIG,
  PAGE_URI,
  PROMOTION_TYPES,
  RENDERER,
  RENDERER_TYPE,
} from '../../constants';
import {
  ContactInfo,
  AddressElement,
  Preferences,
  ContactInformation,
  Rewards,
} from '../../consumerUxlApis/types/contactInformation.types';
import { ITripInfoEdge } from '../../consumerUxlApis/types/trips.types';
import { SessionData } from '../../session';
import dayjs from 'dayjs';
import { getMemState } from '../datalayerHelpers';

const getEncryptedString = async (
  contactInfoData: ContactInfo,
  datalayer: Record<string, string | Date | undefined | boolean | null | number>
) => {
  if (contactInfoData?.customer?.contactInformation?.emails?.[0]?.primary) {
    const utf8 = new TextEncoder()?.encode(contactInfoData?.customer?.contactInformation?.emails?.[0]?.address ?? '');
    const encryptedString = await crypto?.subtle?.digest('SHA-256', utf8)?.then(hashBuffer => {
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(bytes => bytes.toString(16).padStart(2, '0')).join('');
      return hashHex;
    });
    datalayer['ue'] = encryptedString;
  }
};

export const getTodaysDate = (specificTime = '') => {
  const currentAccurateDate = new Date(); // this will return todays date & time accurately
  const justDate = currentAccurateDate?.toISOString()?.split('T')?.[0]; // this will return just date, month & year eg: XXXX/XX/XX
  return new Date(justDate + (specificTime ? 'T' + specificTime : ''));
};

// returns datalayer keys created frm rewards summary UXL query
const getRewardsKeys = (rewardsData: ContactInfo | null | undefined, memState: string) => {
  if (!rewardsData) {
    return {};
  }
  const datalayerkeys = {} as Record<string, string>;
  const rewardsSummary = rewardsData.loyaltyInformation?.rewardsSummary;
  if (!isEmpty(rewardsSummary)) {
    const yearNode = rewardsSummary.yearly;
    if (yearNode?.length) {
      const currentYear = new Date().getFullYear();
      const currentYearNode = yearNode.find((obj: Record<string, any>) => obj['year'] === currentYear);
      if (currentYearNode) {
        datalayerkeys['mr_prof_nights_booked_this_year'] = currentYearNode.totalNights?.toString() ?? '';
        datalayerkeys['bonusNights'] = currentYearNode.nonStayNights?.toString() ?? '';
        // @todo - Logic should ideally be picked from corresponding codes
        // but keeping the logix same as BE code for below 4 keys.
        currentYearNode.nonStayNightsDetails?.forEach((obj: any) => {
          const subtotal = obj.subtotal?.toString();
          if (subtotal) {
            datalayerkeys['mr_prof_nights_promotional'] = subtotal;
            datalayerkeys['mr_prof_nights_cc'] = subtotal;
            datalayerkeys['mr_prof_nights_event'] = subtotal;
            datalayerkeys['rolloverNights'] = subtotal;
          }
        });
      }

      // sum of all the stayNights in yearNode
      datalayerkeys['mr_prof_stays_lifetime'] = yearNode
        .reduce((sum: number, obj: any) => {
          return sum + (typeof obj.stayNights === 'number' ? obj.stayNights : 0);
        }, 0)
        ?.toString();
    }
  }
  const coBrandArray = rewardsData.loyaltyInformation?.programPartners?.coBrands;
  if (memState === AUTH_STATE.loggedIn) {
    datalayerkeys['mr_prof_cc_all'] = coBrandArray?.length
      ? coBrandArray
          .map((obj: any) => obj.partner?.code) // Extract the `code` value from each `partner` object
          .join(',') // Join the `code` values with commas
      : 'none';
  }

  const linkedAccounts = rewardsData.customer?.accounts?.linkedAccounts;
  if (linkedAccounts?.length) {
    datalayerkeys['mr_prof_rewards_linked_accounts'] = 'true';
    // set as true if any of the code has value M else set false
    datalayerkeys['mr_prof_rewards_merged_accounts'] = linkedAccounts
      .some((obj: any) => obj.type?.code === 'M')
      ?.toString();
  } else {
    datalayerkeys['mr_prof_rewards_linked_accounts'] = 'false';
  }

  return datalayerkeys;
};

/**
 *
 * @param consumerData refers to the consumer Response
 * @returns an object that contains
 * key - 1 : promoIDsEligible, which is string, it contains list of ids seperated with comma
 * key - 2 : promoIDsRegistered, which is string, it contains list of ids seperated with comma
 */
export const getConsumerPromotionSummaryKeys = (consumerData: IConsumerDataResponse, memState: string) => {
  const promoIDsEligibleList: string[] = [],
    promoIDsRegisteredList: string[] = [];

  /**
   *
   * @param promotionNode refers to the node object of each and every promotion
   * @returns promotype based on consitios
   *
   * @description returns three types of promotions conditionally, they are
   * case - 1 : returns "newPromotions",
   * when state.code == 'U' & endDate > currentDate & not(offers[0].id should starts with 'MT' && not equal to 'MT004')
   *
   * case - 2 : returns "yourPromotions"
   * when state.code should be in YOUR_PROMOTIONS_STATE_CODES array && endDate > currentDate
   *
   * case - 3 : return "recentPromotions"
   * when state.code either should be in RECENT_PROMOTIONS_STATE_CODES or YOUR_PROMOTIONS_STATE_CODES arrays and
   * difference between end & current date should be greated than 30
   */
  const getPromoType = (promotionNode: IPromotionEdgeNode) => {
    const endDate = new Date(promotionNode?.node?.offers?.[0]?.hurdle?.endDate);
    const endDatePlus_1 = new Date(endDate);
    endDatePlus_1.setDate(endDate?.getDate() + 1);
    const endDateTime = endDate?.toISOString()?.split('T')?.[1];
    const currentDate = getTodaysDate(endDateTime);

    const difference_between_endDate_currentDate_in_days = Math.floor(
      Math.abs(endDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    const recentPromotions_validStateCode = [
      ...CONSUMER_PROMOTION_CONFIG.RECENT_PROMOTIONS_STATE_CODES,
      ...CONSUMER_PROMOTION_CONFIG.YOUR_PROMOTIONS_STATE_CODES,
    ];

    switch (true) {
      case promotionNode?.node?.state?.code === CONSUMER_PROMOTION_CONFIG?.NEW_PROMOTIONS_STATE_CODE &&
        endDatePlus_1.getTime() > currentDate.getTime() &&
        !(
          promotionNode?.node?.offers?.[0]?.id?.startsWith(CONSUMER_PROMOTION_CONFIG?.UNAVAILABLE_PROMOTION_OFFER_ID) &&
          promotionNode?.node?.offers?.[0]?.id !== CONSUMER_PROMOTION_CONFIG?.AVAILABLE_PROMOTION_OFFER_ID
        ):
        return PROMOTION_TYPES.NEW_PROMOTIONS;

      case CONSUMER_PROMOTION_CONFIG?.YOUR_PROMOTIONS_STATE_CODES?.some(code =>
        promotionNode?.node?.state?.code?.includes(code)
      ) && endDatePlus_1.getTime() > currentDate?.getTime():
        return PROMOTION_TYPES.YOUR_PROMOTIONS;

      case recentPromotions_validStateCode.some(code => promotionNode?.node?.state?.code?.includes(code)) &&
        !(difference_between_endDate_currentDate_in_days <= 30) &&
        endDate.getTime() < currentDate.getTime():
        return PROMOTION_TYPES.RECENT_PROMOTIONS;

      default:
        return '';
    }
  };

  consumerData?.contactInformation?.loyaltyInformation?.promotions?.edges?.forEach(promotionNode => {
    const promoType = promotionNode?.node?.progressTracked
      ? getPromoType(promotionNode)
      : PROMOTION_TYPES.PROMOTION_NOT_DISPLAYED;

    if (promoType === PROMOTION_TYPES.NEW_PROMOTIONS) {
      promoIDsEligibleList.push(promotionNode?.node?.id);
    } else if (promoType === PROMOTION_TYPES.YOUR_PROMOTIONS) promoIDsRegisteredList.push(promotionNode?.node?.id);
  });

  return {
    promoIDsEligible: promoIDsEligibleList?.toString(),
    promoIDsRegistered: promoIDsRegisteredList?.toString() || (memState !== AUTH_STATE.guest ? 'null' : ''),
  };
};

const sortCallback = (a: ITripInfoEdge, b: ITripInfoEdge) => {
  const startDate1 = new Date(a?.node?.items?.[0]?.basicInformation?.startDate);
  const startDate2 = new Date(b?.node?.items?.[0]?.basicInformation?.startDate);
  if (startDate1?.getTime() < startDate2?.getTime()) return -1;
  else if (startDate1?.getTime() > startDate2?.getTime()) return 1;
  else {
    const creationDate1 = new Date(a?.node?.items?.[0]?.basicInformation?.creationDate);
    const creationDate2 = new Date(b?.node?.items?.[0]?.basicInformation?.creationDate);
    if (creationDate1?.getTime() < creationDate2?.getTime()) return -1;
    else return 1;
  }
};

const formatDate = (date = '') => {
  return date ? dayjs(date).format('MM/DD/YYYY') : '';
};

export const getConsumerTripsSummaryKeys = (
  consumerData: IConsumerDataResponse,
  sessionData: SessionData,
  renderer: RENDERER_TYPE,
  memState: string
) => {
  if (!consumerData?.contactInformation) {
    return {};
  }
  const consumerTripsSummaryKeys = {} as Record<string, string | undefined>;
  const marshaCode = (sessionData?.data?.cacheData?.data?.['AriesReservation'] as Record<string, string>)?.[
    'propertyId'
  ];
  const inProgressStayNode: ITripInfoEdge[] = [],
    upcomingStayNode: ITripInfoEdge[] = [],
    inProgressTripProperties: string[] = [];

  consumerData?.contactInformation?.searchOrdersByRewardsNumber?.edges?.forEach(edge => {
    const firstNodeItem = edge?.node?.items?.[0];

    const checkInDate = new Date(firstNodeItem?.basicInformation?.startDate);
    const checkOutDate = new Date(firstNodeItem?.basicInformation?.endDate);
    const currentDate = getTodaysDate(checkInDate?.toISOString()?.split('T')?.[1]);

    switch (true) {
      case checkInDate?.getTime() <= currentDate?.getTime() && checkOutDate?.getTime() >= currentDate?.getTime(): // means stay is in progress
        inProgressStayNode.push(edge);
        firstNodeItem?.basicInformation?.confirmationNumber === edge?.node?.id &&
          inProgressTripProperties?.push(firstNodeItem?.property?.id);
        break;
      case checkInDate?.getTime() > currentDate?.getTime(): // means upcoming stay
        upcomingStayNode.push(edge);
        break;
      default:
        break;
    }

    if (inProgressTripProperties?.length > 0) {
      consumerTripsSummaryKeys['mr_prof_rewards_in_progress_properties'] = inProgressTripProperties?.toString();
    }

    if (renderer === RENDERER.BOOK && marshaCode) {
      const properties = consumerTripsSummaryKeys['mr_prof_rewards_in_progress_properties'] ?? '';
      if (properties && properties?.includes(marshaCode)) {
        consumerTripsSummaryKeys['cookie_stay_in_progress'] = 'true';
      } else {
        consumerTripsSummaryKeys['cookie_stay_in_progress'] = 'false';
      }
      delete consumerTripsSummaryKeys['mr_prof_rewards_in_progress_properties'];
    }
  });

  if (inProgressStayNode?.length > 0) {
    inProgressStayNode?.sort(sortCallback);
    consumerTripsSummaryKeys['mr_prof_stay_in_progress'] = 'true';

    if (
      inProgressStayNode?.[0]?.node?.id ===
        inProgressStayNode?.[0]?.node?.items?.[0]?.basicInformation?.confirmationNumber &&
      inProgressStayNode?.[0]?.node?.items?.[0]?.property?.id
    ) {
      consumerTripsSummaryKeys['mr_prof_stay_in_progress_MARSHA'] =
        inProgressStayNode?.[0]?.node?.items?.[0]?.property?.id;
      const propertyInfo = consumerData?.propertiesByIds?.find(
        item => item?.id === inProgressStayNode?.[0]?.node?.items?.[0]?.property?.id
      );
      if (propertyInfo) {
        consumerTripsSummaryKeys['mr_prof_stay_in_progress_hotel'] = propertyInfo?.basicInformation?.name;
        consumerTripsSummaryKeys['mr_prof_stay_in_progress_city'] = propertyInfo?.contactInformation?.address?.city;
      } else {
        consumerTripsSummaryKeys['mr_prof_stay_in_progress'] = 'false';
      }
    }
  } else if (memState === AUTH_STATE.loggedIn) {
    consumerTripsSummaryKeys['mr_prof_stay_in_progress'] = 'false';
  }

  if (upcomingStayNode?.length > 0) {
    upcomingStayNode?.sort(sortCallback);
    consumerTripsSummaryKeys['mr_prof_upcoming_stay'] = 'true';

    const checkInDate = new Date(upcomingStayNode?.[0]?.node?.items?.[0]?.basicInformation?.startDate);
    const currentDate = getTodaysDate(checkInDate?.toISOString()?.split('T')?.[1]);
    const diff_to_checkIn = Math.floor(
      Math.abs(checkInDate?.getTime() - currentDate?.getTime()) / (1000 * 60 * 60 * 24)
    );

    consumerTripsSummaryKeys['mr_prof_upcoming_stay_ci_date'] = formatDate(
      upcomingStayNode?.[0]?.node?.items?.[0]?.basicInformation?.startDate
    );
    consumerTripsSummaryKeys['daysToCheckin'] = String(diff_to_checkIn);
    consumerTripsSummaryKeys['mr_prof_upcoming_stay_co_date'] = formatDate(
      upcomingStayNode?.[0]?.node?.items?.[0]?.basicInformation?.endDate
    );

    if (
      upcomingStayNode?.[0]?.node?.items?.[0]?.basicInformation?.confirmationNumber ===
        upcomingStayNode?.[0]?.node?.id &&
      upcomingStayNode?.[0]?.node?.items?.[0]?.property?.id
    ) {
      consumerTripsSummaryKeys['mr_prof_upcoming_stay_MARSHA'] = upcomingStayNode?.[0]?.node?.items?.[0]?.property?.id;
      const propertyInfo = consumerData?.propertiesByIds?.find(
        item => item?.id === upcomingStayNode?.[0]?.node?.items?.[0]?.property?.id
      );

      if (propertyInfo) {
        consumerTripsSummaryKeys['mr_prof_upcoming_stay_hotel'] = propertyInfo?.basicInformation?.name;
        consumerTripsSummaryKeys['mr_prof_upcoming_stay_city'] = propertyInfo?.contactInformation?.address?.city;
      }
    }
  } else if (memState === AUTH_STATE.loggedIn) {
    consumerTripsSummaryKeys['mr_prof_upcoming_stay'] = 'false';
  }
  return consumerTripsSummaryKeys;
};

const getMemberLevelAndRewardsLevelKeys = (rewards: Rewards, pageURI = '') => {
  const ritzPreferredMember =
    Array.isArray(rewards?.programs) &&
    rewards?.programs?.[0]?.code === 'RCR' &&
    rewards?.programs?.[0]?.preferred === true;

  const membershipLevel = rewards?.level?.code,
    membershipType = rewards?.levelType?.code,
    memberAccount = ritzPreferredMember ? 'RCR' : 'MR';

  let mr_prof_member_program_level = '',
    mr_prof_rewards_level = '';

  switch (true) {
    case membershipLevel == 'P' && membershipType == 'PPR':
      mr_prof_member_program_level = memberAccount + ':' + 'Platinum Premiere';
      mr_prof_rewards_level = 'Platinum Premiere';
      break;
    case membershipLevel == 'P' && membershipType == 'PPZ':
      mr_prof_member_program_level = memberAccount + ':' + 'Platinum Plus';
      mr_prof_rewards_level = 'Platinum Plus';

      break;
    case membershipLevel == 'P' && membershipType == 'PAC':
      mr_prof_rewards_level = 'Platinum Achiever';
      break;
    case membershipLevel == 'P' && membershipType == 'PAR':
      mr_prof_rewards_level = 'Platinum Referral';
      break;
    case membershipLevel == 'P' && membershipType == 'PPC':
      mr_prof_rewards_level = 'Platinum Premier Achiever';
      break;
    case membershipLevel == 'P' && membershipType == 'PFS':
      mr_prof_rewards_level = 'Platinum Premier with Ambassador Achiever';
      break;
    case membershipLevel == 'P' && membershipType == 'PFR':
      mr_prof_rewards_level = 'Platinum Premier with Ambassador Referral';
      break;
    case membershipLevel == 'P' && membershipType == 'PL7':
      mr_prof_rewards_level = 'Platinum Premier Lifetime Elite';
      break;
    case membershipLevel == 'P' && membershipType == 'PLR':
      mr_prof_rewards_level = 'Platinum Lifetime Elite';
      break;
    case membershipLevel == 'P':
      mr_prof_member_program_level = memberAccount + ':' + 'Platinum';
      mr_prof_rewards_level = 'Platinum';
      break;
    case membershipLevel == 'G':
    case membershipLevel == 'S':
      mr_prof_member_program_level = memberAccount + ':' + 'Gold';
      mr_prof_rewards_level = 'Silver';
      break;
    case membershipLevel == 'R':
      mr_prof_member_program_level = memberAccount + ':' + 'Basic';
      mr_prof_rewards_level = 'Basic';
      break;
    case !!membershipLevel && !!membershipType:
      mr_prof_member_program_level = membershipType as string;
      break;
  }

  if (
    (pageURI?.includes(PAGE_URI.loyaltyConvertPoints) || pageURI?.includes(PAGE_URI.loyaltyTransferPoints)) &&
    !!mr_prof_rewards_level
  ) {
    mr_prof_member_program_level = mr_prof_rewards_level;
  }
  return mr_prof_member_program_level || mr_prof_rewards_level
    ? {
        mr_prof_member_program_level,
        mr_prof_rewards_level,
      }
    : {};
};

const getContactInfoKeys = (contactInfoData: ContactInfo | null | undefined, pageURI = '') => {
  if (!contactInfoData) {
    return {};
  }
  let datalayerkeys = {} as Record<string, string | Date | undefined | boolean | null | number>;
  const rewards = contactInfoData.loyaltyInformation?.rewards;
  const customer = contactInfoData.customer;
  getEncryptedString(contactInfoData, datalayerkeys);
  if (rewards) {
    datalayerkeys = {
      mr_id: rewards.number,
      mr_prof_member_level_abbr: rewards.levelType?.code,
      mr_nights_to_renew: rewards.eliteNightsToRenewCurrent,
      mr_prof_nights_to_renew_current: rewards.eliteNightsToRenewCurrent?.toString(),
      mr_nights_to_next_level: rewards.eliteNightsToAchieveNext,
      mr_prof_nights_to_next_level: rewards.eliteNightsToAchieveNext?.toString(),
      mr_prof_points_balance: rewards.currentPointBalance?.toString(),
      mr_prof_nights_lifetime: rewards.eliteLifetimeNights?.toString(),
      mr_nights_booked_lifetime: rewards.eliteLifetimeNights?.toString(),
      mr_prof_earning_pref: rewards.currency?.code,
      mr_prof_join_date: rewards.enrollmentDate,
      mr_prof_preferred_program: rewards.programs?.find(program => program.preferred)?.code || '',
      mr_is_ritz_member: rewards.programs?.some(program => program.code === 'RCR' && program.preferred)?.toString(),
      mr_prof_customer_assocations: rewards.associatedCreditCards?.map(obj => obj.code)?.join(','),
      ...getMemberLevelAndRewardsLevelKeys(rewards, pageURI),
    };
  }
  if (customer) {
    const datalayerObj = {
      mr_prof_gender: customer.profile?.gender?.code,
      mr_prof_name_salutation: customer.profile?.name?.title?.code,
      mr_pref_priority: customer.preferences?.roomPreferencePriority?.code,
      mr_prof_language_chosen: customer.profile?.preferredLanguage?.code,
      mr_prof_saved_prop_count: customer.savedProperties?.length?.toString(),
      mr_prof_saved_prop: customer.savedProperties?.map(obj => obj.id)?.join(','),
      ...getAddressKeys(customer.contactInformation?.addresses),
      ...getPreferencesKeys(customer.preferences),
      ...getMissingDatakeys(customer.contactInformation),
      ...getMobilePhoneDataKey(customer.contactInformation),
    };
    datalayerkeys = { ...datalayerkeys, ...datalayerObj };
  }

  return datalayerkeys;
};

const getAddressKeys = (addresses: AddressElement[] | undefined) => {
  if (!addresses?.length) {
    return {};
  }
  const primaryAddress = addresses.find((addr: AddressElement) => addr.primary === true);
  if (primaryAddress?.address) {
    const { city, stateProvince, country, line1, line2, postalCode } = primaryAddress.address;

    return {
      mr_prof_address_city: city,
      mr_prof_address_state: stateProvince,
      mr_prof_address_state_abbr: stateProvince,
      mr_prof_address_country: country?.code,
      mr_prof_address_country_abbr: country?.code,
      addressLine1Text: line1,
      addressLine2Text: line2,
      addressPostalCode: postalCode,
    };
  }
  return {};
};

const getPreferencesKeys = (preferences: Preferences | undefined) => {
  if (!preferences) {
    return {};
  }
  const datalayerObj = {
    mr_pref_accessibiity: 'false',
    mr_prof_pref_accessible: 'false',
    mr_pref_feather_free: 'false',
    mr_pref_extra_towels: 'false',
    mr_pref_refrig: 'false',
  } as Record<string, string | undefined>;
  preferences.preferences?.forEach(({ category, id }) => {
    switch (category?.code) {
      case 'pillow':
        datalayerObj['mr_pref_pillow'] = id;
        break;
      case 'newspaper':
        datalayerObj['mr_pref_newspaper'] = id;
        break;
      case 'floor':
        datalayerObj['mr_pref_floor'] = id;
        break;
      case 'smoking':
        datalayerObj['mr_pref_room'] = id;
        break;
      case 'accessibility':
        datalayerObj['mr_prof_pref_accessible'] = 'true';
        datalayerObj['mr_pref_accessibiity'] = 'true';
        break;
    }

    switch (id) {
      case 'B5':
        datalayerObj['mr_pref_feather_free'] = 'true';
        break;
      case 'E6':
        datalayerObj['mr_pref_extra_towels'] = 'true';
        break;
      case 'R4':
        datalayerObj['mr_pref_refrig'] = 'true';
        break;
    }
  });
  return datalayerObj;
};

const getMissingDatakeys = (contactInformation: ContactInformation | undefined) => {
  const datalayerObj: Record<string, string | undefined> = {
    mr_prof_email_exists: 'false',
    mr_prof_email_invalid: 'false', // @todo email invalid not present in UXL response
    mr_prof_missing_data: 'false',
  };
  if (!contactInformation) {
    return datalayerObj;
  }
  const missingData: string[] = [];
  const { emails, phones, addresses } = contactInformation;

  const isEmailMissing = !emails?.some(email => email.address);
  datalayerObj['mr_prof_email_exists'] = (!isEmailMissing).toString();
  datalayerObj['mr_prof_email_invalid'] = isEmailMissing.toString();
  if (isEmailMissing) missingData.push('email');

  const isPhoneMissing = !phones?.some(phone => phone.number);
  if (isPhoneMissing) missingData.push('phone');

  const isAddressMissing = !addresses?.some(address => address.address?.country?.code);
  if (isAddressMissing) missingData.push('address');

  if (missingData.length) {
    datalayerObj['mr_prof_missing_data'] = 'true';
    datalayerObj['mr_prof_missing_data_items'] = missingData.join(',');
  }
  return datalayerObj;
};

const getMobilePhoneDataKey = (contactInformation: ContactInformation | undefined) => {
  const hasMobilePhone = contactInformation?.phones?.some(
    phone => phone.number && phone.type?.description === 'Mobile'
  );
  return { mr_prof_mobile_exists: hasMobilePhone?.toString() || 'false' };
};

export const getConsumerUxlKeys = (
  consumerData: IConsumerDataResponse,
  sessionData: SessionData,
  renderer: RENDERER_TYPE,
  pageURI = ''
) => {
  const memState = getMemState(sessionData);
  const rewardskeys = getRewardsKeys(consumerData?.contactInformation, memState);
  const consumerPromotionSummaryKeys = getConsumerPromotionSummaryKeys(consumerData, memState);
  const contactInfoKeys = getContactInfoKeys(consumerData?.contactInformation, pageURI);
  const consumerTripsSummaryKeys = getConsumerTripsSummaryKeys(consumerData, sessionData, renderer, memState);
  const datalayerKeys = {
    ...rewardskeys,
    ...consumerPromotionSummaryKeys,
    ...contactInfoKeys,
    ...consumerTripsSummaryKeys,
  };
  // removing empty and undefined keys
  const filteredObj = Object.fromEntries(
    Object.entries(datalayerKeys).filter(([_, value]) => value != null && value !== '')
  );

  return { ...filteredObj };
};
